import React, { useState } from 'react';

import _ = require('underscore');

import { CompactInlineAlertWidget, DropdownButtonType, InlineAlertType, InlineAlertWidget, List, ListItem, SingleSelectDropdown, TextLinkWidget } from '@tableau/widgets';

import { Filter } from '~commonInfra/utils/Filter';
import { Messages } from '~src/codegen/Localize';
import { ReactActionDialogBodyProps } from '~src/commonInfra/dialogs/ReactActionDialog';
import { HelpService } from '~src/commonInfra/services/HelpService';
import { Tableau } from '~src/commonInfra/utils/TableauLog';
import { showAddCredentialAuthModeDialog } from '~src/connectivity/components/AddCredentialAuthModeDialog';
import { EditConnectionDialogBridgeInfo} from '~src/connectivity/components/EditConnection/components/EditConnectionActionDialogBridgeInfo';
import { EditConnectionDialogPrompt } from '~src/connectivity/components/EditConnection/components/EditConnectionActionDialogPrompt';
import { EditConnectionDialogTestConnection } from '~src/connectivity/components/EditConnection/components/EditConnectionActionDialogTestConnection';
import { OAuthAccountPicker } from '~src/connectivity/components/EditConnection/components/OAuthAccountPicker';
import { EditConnectionDialogProps, EditConnectionMultiRowState, PerConnectionFieldState } from '~src/connectivity/components/EditConnection/EditConnectionAction';
import { EditableConnection } from '~src/connectivity/components/EditConnection/IEditConnectionDialog';
import { AddOAuthAccountFlow } from '~src/connectivity/components/EditConnection/utils/AddOAuthAccountFlow';
import { EditConnectionReactActionUtils } from '~src/connectivity/components/EditConnection/utils/EditConnectionActionUtils';
import { OAuthCredentials } from '~src/connectivity/resources/OAuthCredentials';
import { IAddKeyPairAuthCredentialResult } from '~src/connectivity/serverApi/requests/credentials/AddKeyPairAuthCredentialRequest';
import { IOAuthCredential } from '~src/connectivity/serverApi/types/OAuth/IOAuthCredential';
import { AddKeyPairAuthCredentialAction } from '~src/connectivity/services/AddKeyPairAuthCredentialAction';
import { AddOAuthCredentialAction } from '~src/connectivity/services/AddOAuthCredentialAction';
import { AddServiceAccountFileAction } from '~src/connectivity/services/AddServiceAccountFileCredentialAction';
import { ListResult } from '~src/httpClient/fetchers/ListResult';
import { ResourceQuery } from '~src/httpClient/resources/ResourceQuery';
import { SliceableHelper } from '~src/httpClient/utils/SliceableHelper';
import { learnMoreStyle } from '~src/settings/styles/CommonStyles';

import { EditOAuthConnectionDialogProps, EditOAuthConnectionDialogState } from '../../EditOAuthConnectionAction';
import { EditOAuthConnectionAuthChooser } from './EditOAuthConnectionAuthChooser';
import { EditOAuthConnectionDialogPrompt } from './EditOAuthConnectionDialogPrompt';
import { EditOAuthConnectionDialogTestConnection } from './EditOAuthConnectionDialogTestConnection';

export type EditOAuthConnectionDialogBodyProps = ReactActionDialogBodyProps<
    EditOAuthConnectionDialogProps,
    EditOAuthConnectionDialogState
>;

export function EditOAuthConnectionDialogBody(props: EditOAuthConnectionDialogBodyProps) {

  function updateFormState<K extends keyof EditOAuthConnectionDialogState>(
    field: K,
    value: EditOAuthConnectionDialogState[K]
  ) {
    props.updateDialogState({ [field]: value });
  }

    async function OnAddCredentialForSelectedAuthMode(props: any, state: any, updateDialogState: any, type: string){
        if (state.selectedConnectionAuthMode === Messages.addNewSavedCredential_OAuth()) {
            // if this is just a normal OAuth credential we are adding, then call
            // into attemptAuth to start the oauth flow.
            AddOAuthCredentialAction.attemptAuth(type).then(credentialId => {UpdateSelectedCredential(type, credentialId);});
        } else if (state.selectedConnectionAuthMode === Messages.addNewSavedCredential_ServiceAccount()) {
            // otherwise if this is a service account file credential we are adding,
            // try and parse the file and then store the new credential.
            let result = await AddServiceAccountFileAction
            .addServiceAccountFile(state.connAuthState.file, type, updateDialogState);
            UpdateSelectedCredential(type, result.id);
        } else if (state.selectedConnectionAuthMode === Messages.addNewSavedCredential_KeyPairAuth()) {
            let result = await AddKeyPairAuthCredentialAction
            .addKeyPairAuthCredential(type);
            UpdateSelectedCredential(type, result.id, true);
        }
    }

    async function UpdateSelectedCredential(firstConnectionType: string, credentialId: string, isKeyPairAuth: boolean = false, oauthConfigId?: string) {
        refreshCredentials(firstConnectionType, isKeyPairAuth, oauthConfigId).then((credentialsListResult: ListResult<IOAuthCredential>) => {
            // Set the credentialId if it exists in the list
            if (_.find(credentialsListResult.items,
                (credential: IOAuthCredential) => credential.id === credentialId)) {
                    props.updateDialogState({
                        credentialId: credentialId,
                });
            }
        });
    }

    const refreshCredentials = (
        firstConnectionType: string, isKeyPairAuth: boolean, oauthConfigId?: string) => {
        let credentialsQuery: ResourceQuery<IOAuthCredential> =
            OAuthCredentials.all().addFilter(Filter.fieldEq('type', firstConnectionType));

        if (oauthConfigId) {
            credentialsQuery = credentialsQuery.addFilter(Filter.stringFieldEq('oauthConfigId', oauthConfigId));
        }

        if (isKeyPairAuth) {
            credentialsQuery = credentialsQuery.addFilter(Filter.fieldEq('authentication', 'auth-keypair'));
        }

        return SliceableHelper.fetchAll(credentialsQuery)
            .then((credentialsListResult: ListResult<IOAuthCredential>) => {
                props.updateDialogState({ credentials: isKeyPairAuth ? credentialsListResult.items :
                    credentialsListResult.items.filter(
                    // exclude KeyPair Auth credential from OAuth credential list
                    (c: IOAuthCredential) => c.keypairAuthServer === '')
                });
                return credentialsListResult; // For chaining
            });
    };

    const handleToggleChange = (isOAuthCredentialOption: boolean) => {
        if (isOAuthCredentialOption) {
            props.updateDialogState({ credentialOption: 'oauth' });
        } else {
            props.updateDialogState({ credentialOption: 'none' });
        }
    };

    const handleUsingRemoteQueryAgentChange = (isUsingRemoteQueryAgent: boolean) => {
      props.updateDialogState({ usingRemoteQueryAgent: isUsingRemoteQueryAgent});
    };

    const handleCredentialIdChange = (credentialId: string) => {
        if (credentialId === props.addNewOAuthCredentialOptionValue) {
            props.updateDialogState({
                credentialId: undefined,
            });
            const firstConnection: EditableConnection = props.firstConnection;
            const firstConnectionType = firstConnection.connectionType;
            const firstConnectionDetails = firstConnection.connectionDetails;
            let instanceUrl: string;
            let role: string;
            let oauthConfigId: string;
            let server: string;
            let authentication: string;
            if (firstConnectionDetails.type === 'database') {
                instanceUrl = firstConnectionDetails.instanceUrl;
                role = firstConnectionDetails.role;
                oauthConfigId = firstConnectionDetails.oauthConfigId;
                server = firstConnectionDetails.serverName;
                authentication = firstConnectionDetails.authenticationType;
            }

            if (firstConnectionType === 'bigquery' || firstConnectionType === 'googlebigquery') {
                // TFS1243460 For GBQ, we now support Service Account file credentials AND OAuth, so the user must pick the mode.
                showAddCredentialAuthModeDialog(firstConnectionType, OnAddCredentialForSelectedAuthMode);
            } else if (firstConnectionType === 'snowflake' && authentication === 'auth-keypair')  {
                AddKeyPairAuthCredentialAction.addKeyPairAuthCredential(firstConnectionType).then((result: IAddKeyPairAuthCredentialResult) => {
                    UpdateSelectedCredential(firstConnectionType, result.id, true);
                });
            } else {
                AddOAuthCredentialAction.attemptAuth(firstConnectionType, instanceUrl, role, oauthConfigId, server).then((credentialId: string) =>{
                    UpdateSelectedCredential(firstConnectionType, credentialId, false, oauthConfigId);
                });
            }
        } else {
            props.updateDialogState({ credentialId: credentialId });
        }
    };

    const learnMoreLink = (
      <TextLinkWidget
        className={learnMoreStyle}
        href={HelpService.getServerHelpUrlByCategory('managed-keychain')}
        target='_blank'
        testId={'learn-more'}
      >
        {Messages.learnMore()}
      </TextLinkWidget>
    );

    return (
        <>
            <EditOAuthConnectionDialogPrompt prompt={props.prompt} />
            <div style={{ width: '100%' }}>
                <OAuthAccountPicker
                    credentialNoValueOptionText={props.dialogState.credentialNoValueOptionText}
                    currentConnectionTypeDisplayName={props.currentConnectionTypeDisplayName}
                    addNewOAuthCredentialOptionValue={props.addNewOAuthCredentialOptionValue}
                    credentialId={props.dialogState.credentialId}
                    credentials={props.dialogState.credentials}
                    credentialOption={props.dialogState.credentialOption}
                    handleToggleChange={handleToggleChange}
                    handleCredentialIdChange={handleCredentialIdChange}
                />

                {props.dialogState.usingManagedKeychain && <InlineAlertWidget
                  testId={'managed-keychain-alert'}
                  type={InlineAlertType.Warning}
                  children={<span>
                    {Messages.managedKeychainEditConnectionWarningMessage()}
                    {learnMoreLink}
                  </span>}/>}

                <EditConnectionDialogBridgeInfo
                  usingRemoteQueryAgent={props.dialogState.usingRemoteQueryAgent}
                  handleUsingRemoteQueryAgentChange={handleUsingRemoteQueryAgentChange}
                  showNetworkType={props.showNetworkType}
                />

                <EditOAuthConnectionDialogTestConnection
                    dialogState={props.dialogState}
                    onTestConnection={props.testConnection}
                    isTestConnectionDisabled={props.isTestConnectionDisabled(props.dialogState)}
                />
            </div>
        </>
    );
}

export type EditOAuthConnectionWifDialogBodyProps = ReactActionDialogBodyProps<
    EditConnectionDialogProps,
    EditConnectionMultiRowState
>;

// W-23547170: the WIF-eligible body for the OAuth-specific dialog. It follows the unified body's
// selector-plus-one-editor shape (a connection selector over the selected rows plus exactly one
// chooser for the shown connection), because a WIF-eligible selection is per-connection: each
// connection carries its own workgroupAuthMode / service account / stored-WIF flag and Saves as its
// own per-id update. Alongside the chooser it renders the network-type/bridge toggle and Test as
// siblings, matching the stock OAuth body; only Snowflake server/port/username field inputs are
// scoped out (OD-3). Test is always disabled for a WIF row (isTestConnectionDisabledForAuth).
export function EditOAuthConnectionWifDialogBody(props: EditOAuthConnectionWifDialogBodyProps) {
    // The dropdown selection is local view state, not a staged edit: every row's edits live in
    // props.dialogState.rows, so switching the selection never discards a hidden connection's edits.
    const [selectedIndex, setSelectedIndex] = useState(0);

    // Apply several field changes to one row in a single write. The chooser sets multiple fields at
    // once when a radio is picked; writing them one at a time would let only the last survive because
    // each single-field write starts from the same pre-change row.
    function updateRowFields(connectionId: string, changes: Partial<PerConnectionFieldState>) {
        const rows = props.dialogState.rows.map((row) =>
            row.connectionId === connectionId ? { ...row, ...changes } : row);
        props.updateDialogState({ rows });
    }

    function updateRowState<K extends keyof PerConnectionFieldState>(
        connectionId: string, field: K, value: PerConnectionFieldState[K]) {
        updateRowFields(connectionId, { [field]: value } as Partial<PerConnectionFieldState>);
    }

    // The router guarantees at least one connection; guard anyway so an empty selection cannot deref
    // props.rows[-1].
    if (props.rows.length === 0) {
        return null;
    }
    const selected = Math.min(selectedIndex, props.rows.length - 1);
    const showSelector = props.rows.length > 1;
    const rowProps = props.rows[selected];
    const rowState = props.dialogState.rows[selected];
    const id = rowProps.connectionId;

    // Save is gated on every row, including hidden ones. Surface which hidden connection blocks Save
    // so the disabled button is not a mystery.
    const rowValidity = props.dialogState.rows.map((state) => EditConnectionReactActionUtils.isWifStateValid(state));
    const hasHiddenInvalidRow = rowValidity.some((valid, index) => !valid && index !== selected);

    const selectorItems: ListItem[] = props.rows.map((row, index) => ({
        content: rowValidity[index]
            ? row.rowLabel
            : row.rowLabel + ' ' + Messages.editConnection_connectionSelector_needsAttention(),
        value: String(index)
    }));
    const selectedValue = String(selected);

    return (
        <>
            <EditConnectionDialogPrompt prompt={props.prompt} />
            {showSelector ? (
                <SingleSelectDropdown
                    style={{ width: '100%' }}
                    value={selectedValue}
                    onChange={(value: string) => setSelectedIndex(Number(value))}
                    testId={'edit-conn-connection-selector'}
                    data-tb-test-id={'edit-conn-connection-selector'}
                    list={<List items={selectorItems} selectedValue={selectedValue} />}
                    label={Messages.editConnection_connectionSelector_label()}
                    type={DropdownButtonType.Outline}
                />
            ) : null}
            {showSelector && hasHiddenInvalidRow ? (
                <CompactInlineAlertWidget
                    children={Messages.editConnection_connectionSelector_invalidHint()}
                    type={InlineAlertType.Warning}
                    testId={'edit-conn-hidden-invalid-hint'}
                />
            ) : null}
            <div style={{ width: '100%' }} data-tb-test-id={'edit-conn-row-' + id}>
                <EditOAuthConnectionAuthChooser
                    workgroupAuthMode={rowState.workgroupAuthMode}
                    authFederationServiceAccount={rowState.authFederationServiceAccount}
                    credentialOption={rowState.credentialOption}
                    credentialId={rowState.credentialId}
                    credentials={rowState.credentials}
                    credentialNoValueOptionText={rowState.credentialNoValueOptionText}
                    isOAuthEmbeddedApplicable={rowProps.isOAuthEmbeddedApplicable}
                    wifModesAvailable={rowProps.wifModesAvailable}
                    isStoredWif={rowProps.isStoredWif}
                    usingManagedKeychain={rowProps.usingManagedKeychain}
                    addNewOAuthCredentialOptionValue={rowProps.addNewOAuthCredentialOptionValue}
                    currentConnectionTypeDisplayName={rowProps.currentConnectionTypeDisplayName}
                    onSelectRadio={(change) => updateRowFields(id, change as Partial<PerConnectionFieldState>)}
                    onServiceAccountChange={(value) => updateRowState(id, 'authFederationServiceAccount', value)}
                    handleCredentialIdChange={(credentialId) => {
                        if (credentialId === rowProps.addNewOAuthCredentialOptionValue) {
                            // Selecting "Add an Account…" runs the add-credential flow for this
                            // connection, then updates the row with the reloaded account list and the
                            // newly created account. The flow's dialog rejects on cancel/dismiss (the
                            // routine back-out path); swallow it but log at debug so a genuine failure
                            // is still traceable.
                            AddOAuthAccountFlow.addAccount(rowProps.connection).then((result) =>
                                updateRowFields(id, { credentials: result.credentials, credentialId: result.credentialId }))
                                .catch((reason) => Tableau.Logger().warn('Add-account flow dismissed or failed: ' + reason));
                        } else {
                            updateRowState(id, 'credentialId', credentialId);
                        }
                    }}
                />

                <EditConnectionDialogBridgeInfo
                    usingRemoteQueryAgent={rowState.usingRemoteQueryAgent}
                    handleUsingRemoteQueryAgentChange={(value) => updateRowState(id, 'usingRemoteQueryAgent', value)}
                    showNetworkType={rowProps.showNetworkType}
                />

                {rowProps.isTestConnectionButtonVisible ? (
                    <EditConnectionDialogTestConnection
                        dialogState={rowState}
                        onTestConnection={rowProps.onTestConnection}
                        usesAuthChooser={EditConnectionReactActionUtils.usesAuthChooser(rowProps)}
                    />
                ) : null}
            </div>
        </>
    );
}
